import React, { useState, useEffect, useCallback } from 'react';
import { getBaseURL } from '../config/api';
import axios from 'axios';


const api = axios.create({
  baseURL: `${getBaseURL()}/api`,
  withCredentials: true,
});

const ImageGenerator = () => {
  const [descriptions, setDescriptions] = useState('');
  const [style, setStyle] = useState('food_photo');
  const [format, setFormat] = useState('square');
  const [sourceImage, setSourceImage] = useState(null);
  const [sourceImagePreview, setSourceImagePreview] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [results, setResults] = useState([]);
  const [errors, setErrors] = useState([]);
  const [generatedImages, setGeneratedImages] = useState([]);
  const [options, setOptions] = useState({ styles: [], formats: [] });
  const [activeTab, setActiveTab] = useState('generate'); // 'generate' or 'gallery'
  const [error, setError] = useState('');
  const [previewImage, setPreviewImage] = useState(null); // For lightbox preview
  const [galleryFilter, setGalleryFilter] = useState(''); // Filter by filename/prompt

  // Load options and generated images on mount
  useEffect(() => {
    loadOptions();
    loadGeneratedImages();
  }, []);

  const loadOptions = async () => {
    try {
      const response = await api.get('/image-generator/options');
      setOptions(response.data);
    } catch (err) {
      console.error('Error loading options:', err);
      setError('Failed to load options. Make sure you are logged in as admin.');
    }
  };

  const loadGeneratedImages = async () => {
    try {
      const response = await api.get('/image-generator/generated');
      setGeneratedImages(response.data.images || []);
    } catch (err) {
      console.error('Error loading generated images:', err);
    }
  };

  const handleSourceImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSourceImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setSourceImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeSourceImage = () => {
    setSourceImage(null);
    setSourceImagePreview(null);
  };

  const handleGenerate = async () => {
    if (!descriptions.trim()) {
      setError('Please enter at least one description');
      return;
    }

    setIsGenerating(true);
    setResults([]);
    setErrors([]);
    setError('');

    const descriptionList = descriptions.split(/[\r\n]+/).filter(d => d.trim());
    setProgress({ current: 0, total: descriptionList.length });

    const formData = new FormData();
    formData.append('descriptions', descriptions);
    formData.append('style', style);
    formData.append('format', format);
    
    if (sourceImage) {
      formData.append('sourceImage', sourceImage);
    }

    try {
      const response = await api.post('/image-generator/generate', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setResults(response.data.results || []);
      setErrors(response.data.errors || []);
      setProgress({ current: response.data.generated, total: response.data.totalRequested });
      
      // Refresh the gallery
      loadGeneratedImages();
    } catch (err) {
      console.error('Generation error:', err);
      setError(err.response?.data?.error || 'Failed to generate images');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDeleteImage = async (filename) => {
    if (!window.confirm('Are you sure you want to delete this image?')) {
      return;
    }

    try {
      await api.delete(`/image-generator/generated/${filename}`);
      loadGeneratedImages();
    } catch (err) {
      console.error('Delete error:', err);
      setError('Failed to delete image');
    }
  };

  const downloadImage = (url, filename) => {
    const link = document.createElement('a');
    link.href = getBaseURL() + url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="max-w-6xl mx-auto p-4">
      <h1 className="text-2xl font-bold mb-6 text-center">🎨 AI Image Generator</h1>
      <p className="text-gray-400 text-center mb-6">
        Generate brand-consistent images for El Barrio using Google Imagen AI
      </p>

      {/* Tabs */}
      <div className="flex mb-6 border-b border-gray-700">
        <button
          className={`px-6 py-3 font-medium ${activeTab === 'generate' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-gray-400 hover:text-white'}`}
          onClick={() => setActiveTab('generate')}
        >
          Generate Images
        </button>
        <button
          className={`px-6 py-3 font-medium ${activeTab === 'gallery' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-gray-400 hover:text-white'}`}
          onClick={() => setActiveTab('gallery')}
        >
          Gallery ({generatedImages.length})
        </button>
      </div>

      {error && (
        <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded mb-4">
          {error}
          <button onClick={() => setError('')} className="float-right text-red-300 hover:text-white">×</button>
        </div>
      )}

      {activeTab === 'generate' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Input Form */}
          <div className="space-y-4">
            {/* Descriptions */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Image Descriptions
                <span className="text-gray-400 text-xs ml-2">(one per line for batch generation)</span>
              </label>
              <textarea
                value={descriptions}
                onChange={(e) => setDescriptions(e.target.value)}
                placeholder="Enter descriptions, one per line. Add (filename) at end to set custom filename:&#10;A delicious burrito with fresh ingredients (surf-n-turf)&#10;Colorful tacos on a rustic plate (street-tacos)&#10;Refreshing horchata drink"
                className="w-full p-3 rounded bg-gray-800 border border-gray-700 text-white placeholder-gray-500 h-40"
                disabled={isGenerating}
              />
              <p className="text-xs text-gray-500 mt-1">
                {descriptions.split(/[\r\n]+/).filter(d => d.trim()).length} description(s)
              </p>
            </div>

            {/* Style Selection */}
            <div>
              <label className="block text-sm font-medium mb-2">Style</label>
              <select
                value={style}
                onChange={(e) => setStyle(e.target.value)}
                className="w-full p-3 rounded bg-gray-800 border border-gray-700 text-white"
                disabled={isGenerating}
              >
                {options.styles.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
              {options.styles.find(s => s.id === style) && (
                <p className="text-xs text-gray-500 mt-1">
                  {options.styles.find(s => s.id === style)?.description}
                </p>
              )}
            </div>

            {/* Format Selection */}
            <div>
              <label className="block text-sm font-medium mb-2">Format / Size</label>
              <div className="grid grid-cols-2 gap-2">
                {options.formats.map(f => (
                  <button
                    key={f.id}
                    onClick={() => setFormat(f.id)}
                    disabled={isGenerating}
                    className={`p-3 rounded border text-left ${
                      format === f.id 
                        ? 'border-blue-500 bg-blue-500/20 text-blue-300' 
                        : 'border-gray-700 bg-gray-800 hover:border-gray-600'
                    }`}
                  >
                    <div className="font-medium text-sm">{f.name}</div>
                    <div className="text-xs text-gray-500">{f.aspectRatio} • {f.dimensions}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Source Image Upload - TEMPORARILY DISABLED - Reference image feature pending API support
            <div>
              <label className="block text-sm font-medium mb-2">
                Reference Image (Optional)
                <span className="text-gray-400 text-xs ml-2">for style inspiration</span>
              </label>
              {!sourceImagePreview ? (
                <div className="border-2 border-dashed border-gray-700 rounded p-6 text-center hover:border-gray-600 cursor-pointer">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleSourceImageChange}
                    className="hidden"
                    id="source-image-input"
                    disabled={isGenerating}
                  />
                  <label htmlFor="source-image-input" className="cursor-pointer">
                    <div className="text-gray-400">
                      <svg className="mx-auto h-12 w-12 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p>Click to upload a reference image</p>
                      <p className="text-xs mt-1">JPEG, PNG, or WebP (max 10MB)</p>
                    </div>
                  </label>
                </div>
              ) : (
                <div className="relative inline-block">
                  <img
                    src={sourceImagePreview}
                    alt="Source preview"
                    className="max-h-40 rounded border border-gray-700"
                  />
                  <button
                    onClick={removeSourceImage}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600"
                    disabled={isGenerating}
                  >
                    ×
                  </button>
                </div>
              )}
            </div>
            */}

            {/* Generate Button */}
            <button
              onClick={handleGenerate}
              disabled={isGenerating || !descriptions.trim()}
              className={`w-full py-4 rounded font-bold text-lg ${
                isGenerating || !descriptions.trim()
                  ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white'
              }`}
            >
              {isGenerating ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Generating ({progress.current}/{progress.total})...
                </span>
              ) : (
                `🎨 Generate ${descriptions.split(/[\r\n]+/).filter(d => d.trim()).length || ''} Image${descriptions.split(/[\r\n]+/).filter(d => d.trim()).length !== 1 ? 's' : ''}`
              )}
            </button>
          </div>

          {/* Right Column - Results */}
          <div>
            <h2 className="text-lg font-medium mb-4">Generated Results</h2>
            
            {results.length === 0 && errors.length === 0 && !isGenerating && (
              <div className="bg-gray-800 rounded p-8 text-center text-gray-500">
                <svg className="mx-auto h-16 w-16 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p>Generated images will appear here</p>
              </div>
            )}

            {/* Success Results */}
            {results.length > 0 && (
              <div className="space-y-4">
                {results.map((result, idx) => (
                  <div key={idx} className="bg-gray-800 rounded overflow-hidden">
                    <img
                      src={getBaseURL() + result.url}
                      alt={result.description}
                      className="w-full"
                    />
                    <div className="p-3">
                      <p className="text-sm text-gray-300 mb-2">{result.originalPrompt}</p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => downloadImage(result.url, result.filename)}
                          className="text-xs bg-blue-600 hover:bg-blue-700 px-3 py-1 rounded"
                        >
                          Download
                        </button>
                        <button
                          onClick={() => window.open(getBaseURL() + result.url, '_blank')}
                          className="text-xs bg-gray-700 hover:bg-gray-600 px-3 py-1 rounded"
                        >
                          View Full Size
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Errors */}
            {errors.length > 0 && (
              <div className="mt-4 space-y-2">
                <h3 className="text-red-400 font-medium">Failed ({errors.length})</h3>
                {errors.map((err, idx) => (
                  <div key={idx} className="bg-red-900/30 border border-red-800 rounded p-3">
                    <p className="text-sm text-gray-300">{err.description}</p>
                    <p className="text-xs text-red-400 mt-1">{err.error}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'gallery' && (
        <div>
          {/* Filter Input */}
          <div className="mb-4">
            <input
              type="text"
              value={galleryFilter}
              onChange={(e) => setGalleryFilter(e.target.value)}
              placeholder="🔍 Filter by filename or prompt..."
              className="w-full p-3 rounded bg-gray-800 border border-gray-700 text-white placeholder-gray-500"
            />
          </div>

          {generatedImages.length === 0 ? (
            <div className="bg-gray-800 rounded p-8 text-center text-gray-500">
              <p>No generated images yet. Create some!</p>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-500 mb-4">
                Showing {generatedImages.filter(img => 
                  !galleryFilter || 
                  img.filename.toLowerCase().includes(galleryFilter.toLowerCase()) ||
                  (img.originalPrompt && img.originalPrompt.toLowerCase().includes(galleryFilter.toLowerCase()))
                ).length} of {generatedImages.length} images
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {generatedImages
                  .filter(img => 
                    !galleryFilter || 
                    img.filename.toLowerCase().includes(galleryFilter.toLowerCase()) ||
                    (img.originalPrompt && img.originalPrompt.toLowerCase().includes(galleryFilter.toLowerCase()))
                  )
                  .map((img, idx) => (
                  <div key={idx} className="bg-gray-800 rounded overflow-hidden">
                    {/* Image with click to preview */}
                    <div 
                      className="relative cursor-pointer"
                      onClick={() => setPreviewImage(img)}
                    >
                      <img
                        src={getBaseURL() + img.url}
                        alt={img.filename}
                        className="w-full h-48 object-cover hover:opacity-90 transition-opacity"
                      />
                      <div className="absolute bottom-2 right-2 bg-black/70 px-2 py-1 rounded text-xs">
                        Click to preview
                      </div>
                    </div>
                    
                    {/* Image Info */}
                    <div className="p-3">
                      <p className="text-sm font-medium text-white truncate">{img.filename}</p>
                      {img.originalPrompt && (
                        <p className="text-xs text-gray-400 mt-1 line-clamp-2" title={img.originalPrompt}>
                          💬 {img.originalPrompt}
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                        <span>{formatFileSize(img.size)}</span>
                        <span>•</span>
                        <span>{new Date(img.createdAt).toLocaleDateString()}</span>
                        {img.style && (
                          <>
                            <span>•</span>
                            <span className="text-blue-400">{img.style.replace(/_/g, ' ')}</span>
                          </>
                        )}
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex gap-2 mt-3">
                        <button
                          onClick={() => setPreviewImage(img)}
                          className="flex-1 text-xs bg-gray-700 hover:bg-gray-600 px-3 py-2 rounded"
                        >
                          🔍 Preview
                        </button>
                        <button
                          onClick={() => downloadImage(img.url, img.filename)}
                          className="flex-1 text-xs bg-blue-600 hover:bg-blue-700 px-3 py-2 rounded"
                        >
                          ⬇ Download
                        </button>
                        <button
                          onClick={() => handleDeleteImage(img.filename)}
                          className="text-xs bg-red-600 hover:bg-red-700 px-3 py-2 rounded"
                        >
                          🗑
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Preview Modal */}
      {previewImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setPreviewImage(null)}
        >
          <div 
            className="max-w-4xl max-h-full overflow-auto bg-gray-900 rounded-lg"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute top-4 right-4 text-white bg-gray-800 hover:bg-gray-700 rounded-full w-10 h-10 flex items-center justify-center text-xl"
            >
              ×
            </button>
            
            {/* Full size image */}
            <img
              src={getBaseURL() + previewImage.url}
              alt={previewImage.filename}
              className="max-w-full max-h-[70vh] object-contain"
            />
            
            {/* Image details */}
            <div className="p-4 border-t border-gray-700">
              <h3 className="text-lg font-medium text-white mb-2">{previewImage.filename}</h3>
              
              {previewImage.originalPrompt && (
                <div className="mb-3">
                  <span className="text-xs text-gray-500">Original Prompt:</span>
                  <p className="text-sm text-gray-300 mt-1">{previewImage.originalPrompt}</p>
                </div>
              )}
              
              {previewImage.enhancedPrompt && (
                <div className="mb-3">
                  <span className="text-xs text-gray-500">Enhanced Prompt:</span>
                  <p className="text-xs text-gray-400 mt-1">{previewImage.enhancedPrompt}</p>
                </div>
              )}
              
              <div className="flex flex-wrap gap-4 text-sm text-gray-400 mb-4">
                <span>📅 {new Date(previewImage.createdAt).toLocaleString()}</span>
                <span>📦 {formatFileSize(previewImage.size)}</span>
                {previewImage.style && <span>🎨 {previewImage.style.replace(/_/g, ' ')}</span>}
                {previewImage.format && <span>📐 {previewImage.format.replace(/_/g, ' ')}</span>}
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => downloadImage(previewImage.url, previewImage.filename)}
                  className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded font-medium"
                >
                  ⬇ Download
                </button>
                <button
                  onClick={() => window.open(getBaseURL() + previewImage.url, '_blank')}
                  className="bg-gray-700 hover:bg-gray-600 px-4 py-2 rounded"
                >
                  Open in New Tab
                </button>
                <button
                  onClick={() => {
                    handleDeleteImage(previewImage.filename);
                    setPreviewImage(null);
                  }}
                  className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded"
                >
                  🗑 Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageGenerator;
